import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Space,
  Tag,
  Divider,
} from "antd";
import axiosInstance from "../api/axiosInstance";
import styled from "styled-components";

const { Option } = Select;

// Styled components (keep your existing styles)
const StyledModal = styled(Modal)`
  /* ... your existing styles ... */
`;

const CompactFormItem = styled(Form.Item)`
  /* ... your existing styles ... */
`;

const ActionButtons = styled(Space)`
  /* ... your existing styles ... */
`;

const PriceInfo = styled.div`
  /* ... your existing styles ... */
`;

const RoomEditModal = ({ open, visible, room, onCancel, onSuccess, roomTypes }) => {
  const [form] = Form.useForm();

  // Support both props during migration
  const isOpen = open ?? visible ?? false;

  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);

  const [currentPrice, setCurrentPrice] = useState({
    basePrice: 0,
    currency: "BWP",
    vatPercentage: 0,
    additionalLevies: 0,
    discount: 0,
  });

  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);

  useEffect(() => {
    if (isOpen && room) {
      form.setFieldsValue({
        roomNumber: room.roomNumber ?? room.RoomNumber,
        roomTypeId: room.roomTypeId ?? room.RoomTypeId,
        floor: room.floor ?? room.Floor,
        capacity: room.capacity ?? room.Capacity,
        features: room.features ?? room.Features,
        description: room.description ?? room.Description,
      });

      fetchRoomPrice(room.roomId ?? room.RoomId, room.hotelId ?? room.HotelId);
    }
  }, [isOpen, room, form]);

  useEffect(() => {
    if (Array.isArray(roomTypes) && roomTypes.length > 0) {
      setAvailableRoomTypes(roomTypes.filter((rt) => (rt?.roomTypeId ?? 0) > 0));
    } else {
      setAvailableRoomTypes([]);
    }
  }, [roomTypes]);

  const fetchRoomPrice = async (roomId, hotelId) => {
    if (!roomId || !hotelId) return;

    try {
      setPriceLoading(true);

      const response = await axiosInstance.get(
        `/Price/roomprice?hotelId=${hotelId}&roomId=${roomId}`
      );

      if (response.data) {
        setCurrentPrice({
          basePrice: response.data.price || response.data.basePrice || 0,
          currency: response.data.currency || "BWP",
          vatPercentage: response.data.vatPercentage || 0,
          additionalLevies: response.data.additionalLevies || 0,
          discount: response.data.discount || 0,
        });
      } else {
        setCurrentPrice({
          basePrice: 0,
          currency: "BWP",
          vatPercentage: 0,
          additionalLevies: 0,
          discount: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching room price:", error);
      setCurrentPrice({
        basePrice: 0,
        currency: "BWP",
        vatPercentage: 0,
        additionalLevies: 0,
        discount: 0,
      });
    } finally {
      setPriceLoading(false);
    }
  };

  const handleRoomTypeChange = async (roomTypeId) => {
    if (!room?.hotelId && !room?.HotelId) return;

    try {
      setPriceLoading(true);

      const hotelId = room.hotelId ?? room.HotelId;

      const response = await axiosInstance.get(
        `/Price/hotel/${hotelId}?roomTypeId=${roomTypeId}`
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const priceData = { ...response.data[0] };

        if (!priceData.basePrice && !priceData.price) {
          const defaultPrice = await axiosInstance.get(
            `/Price/default?roomTypeId=${roomTypeId}`
          );
          priceData.basePrice = defaultPrice.data?.basePrice || 1000;
        }

        setCurrentPrice({
          basePrice: priceData.basePrice ?? priceData.price ?? 0,
          currency: priceData.currency || "BWP",
          vatPercentage: priceData.vatPercentage || 0,
          additionalLevies: priceData.additionalLevies || 0,
          discount: priceData.discount || 0,
        });
      }
    } catch (error) {
      console.error("Error loading price:", error);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!room) return;

    try {
      setLoading(true);

      const values = await form.validateFields();

      const payload = {
        roomNumber: values.roomNumber,
        roomTypeId: values.roomTypeId,
        floor: values.floor,
        capacity: values.capacity,
        features: values.features,
        description: values.description,
      };

      const roomId = room.roomId ?? room.RoomId;

      await axiosInstance.put(`/Rooms/UpdateRoom/${roomId}`, payload);

      message.success("Room updated successfully");
      onSuccess?.();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update room");
    } finally {
      setLoading(false);
    }
  };

  const calculatePriceDetails = () => {
    const { basePrice, additionalLevies, vatPercentage, discount, currency } =
      currentPrice;

    const base = Number(basePrice ?? 0);
    const fees = Number(additionalLevies ?? 0);
    const vatPct = Number(vatPercentage ?? 0);
    const discountPct = Number(discount ?? 0);

    const totalBeforeVat = base + fees;
    const vatAmount = totalBeforeVat * (vatPct / 100);
    const totalWithVat = totalBeforeVat + vatAmount;

    const discountAmount =
      discountPct > 0 ? totalWithVat * (discountPct / 100) : 0;

    const finalPrice = totalWithVat - discountAmount;

    return {
      base,
      fees,
      vat: vatAmount,
      totalBeforeDiscount: totalWithVat,
      discountAmount,
      finalPrice,
      currency: currency || "BWP",
      discount: discountPct,
      vatPct,
    };
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { BWP: "P", USD: "$", EUR: "€", GBP: "£" };
    const n = Number(amount ?? 0);
    return `${symbols[currency] || currency} ${n.toFixed(2)}`;
  };

  const priceDetails = calculatePriceDetails();

  return (
    <StyledModal
      title={`Edit Room - ${room?.roomNumber ?? room?.RoomNumber ?? ""}`}
      // visible -> open
      open={isOpen}
      onCancel={onCancel}
      width={650}
      // destroyOnClose -> destroyOnHidden
      destroyOnHidden
      footer={
        <ActionButtons>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            Save Changes
          </Button>
        </ActionButtons>
      }
    >
      <Form form={form} layout="vertical">
        <CompactFormItem
          name="roomNumber"
          label="Room Number"
          rules={[{ required: true, message: "Please enter room number" }]}
        >
          <Input />
        </CompactFormItem>

        <CompactFormItem
          name="roomTypeId"
          label="Room Type"
          rules={[{ required: true, message: "Please select room type" }]}
        >
          <Select
            placeholder="Select room type"
            onChange={handleRoomTypeChange}
            loading={priceLoading}
          >
            {availableRoomTypes.map((type) => (
              <Option key={type.roomTypeId} value={type.roomTypeId}>
                {type.typeName || type.roomTypeName}
              </Option>
            ))}
          </Select>
        </CompactFormItem>

        <PriceInfo>
          <Divider orientation="left" style={{ marginTop: 0 }}>
            Pricing Information
          </Divider>

          <div className="price-row">
            <span>Base Price:</span>
            <span>{formatCurrency(priceDetails.base, priceDetails.currency)}</span>
          </div>

          <div className="price-row">
            <span>Additional Fees:</span>
            <span>{formatCurrency(priceDetails.fees, priceDetails.currency)}</span>
          </div>

          <div className="price-row">
            <span>VAT ({priceDetails.vatPct}%):</span>
            <span>{formatCurrency(priceDetails.vat, priceDetails.currency)}</span>
          </div>

          {priceDetails.discount > 0 && (
            <div className="price-row">
              <span>Discount ({priceDetails.discount}%):</span>
              <span>
                -{formatCurrency(priceDetails.discountAmount, priceDetails.currency)}
              </span>
            </div>
          )}

          <div className="price-row total-price">
            <span>Total Price:</span>
            <span>
              <Tag color={priceDetails.discount > 0 ? "green" : "blue"}>
                {formatCurrency(priceDetails.finalPrice, priceDetails.currency)}
              </Tag>
            </span>
          </div>
        </PriceInfo>

        <CompactFormItem name="floor" label="Floor">
          <InputNumber min={1} style={{ width: "100%" }} />
        </CompactFormItem>

        <CompactFormItem name="capacity" label="Capacity">
          <InputNumber min={1} style={{ width: "100%" }} />
        </CompactFormItem>

        <CompactFormItem name="features" label="Features">
          <Input.TextArea
            rows={2}
            placeholder="Enter room features separated by commas"
          />
        </CompactFormItem>

        <CompactFormItem name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Enter room description" />
        </CompactFormItem>
      </Form>
    </StyledModal>
  );
};

export default RoomEditModal;
